import { AppInitHelper } from './../../../../services/helpers/app-init.helper';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { UserModel } from '../../../../models/user/user.model';
import { DashboardHelper } from '../../../../services/dashboard.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeedbackComponent } from '../../../feedback/feedback.component';
import { UserStore } from '../../../../services/stores/user.store';
import { UserService } from '../../../../services/user.service';
import { PermissionService } from '../../../../services/permission.service';
import { ADMIN_SECTION_PERMISSIONS } from '../../../../modules/catch-core/data/constants/permission.constants';
import * as PermissionConstants from '../../../../modules/catch-core/data/constants/permission.constants';
import { CHATBOT_AUTO_CLOSE_ROUTES } from '../../../../modules/catch-core/data/constants/route.constants';

@Component({
  selector: 'app-toolbar-user-menu',
  templateUrl: './toolbar-user-menu.component.html',
  styleUrls: ['./toolbar-user-menu.component.css'],
  standalone: false,
})
export class ToolbarUserMenuComponent extends BaseComponent implements OnInit {
  private user: UserModel | null = null;
  public isSysAdmin = false;
  public adminSectionPermissions = ADMIN_SECTION_PERMISSIONS;
  public readonly permission = PermissionConstants;

  // Snapshotted once in ngOnInit (like isSysAdmin) rather than called from the template -
  // this menu lives in the header, so its bindings re-evaluate on every CD pass.
  public canReadOrg = false;
  public canReadUser = false;
  public canReadGroup = false;
  public canReadRole = false;
  public canReadPermission = false;
  public canReadLicense = false;
  public canCreateExternalLink = false;
  public canReadApiKey = false;
  public canReadTemplateAll = false;
  public canReadAttachment = false;
  public canReadLog = false;
  constructor(
    public permissionService: PermissionService,
    private userService: UserService,
    private userStore: UserStore,
    private router: Router,
    private dashboardHelper: DashboardHelper,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private appInitHelper: AppInitHelper,
  ) {
    super();
  }

  ngOnInit() {
    this.user = this.userService.user;
    this.isSysAdmin = this.permissionService.isSysAdmin;
    this.canReadOrg = this.permissionService.hasPermission(this.permission.ORG_READ);
    this.canReadUser = this.permissionService.hasPermission(this.permission.USER_READ);
    this.canReadGroup = this.permissionService.hasPermission(this.permission.GROUP_READ);
    this.canReadRole = this.permissionService.hasPermission(this.permission.ROLE_READ);
    this.canReadPermission = this.permissionService.hasPermission(this.permission.PERMISSION_READ);
    this.canReadLicense = this.permissionService.hasPermission(this.permission.LICENSE_READ);
    this.canCreateExternalLink = this.permissionService.hasPermission(this.permission.EXTERNAL_LINK_CREATE);
    this.canReadApiKey = this.permissionService.hasPermission(this.permission.APIKEY_READ);
    // ZOO-10032: template:read_all is the cross-org blueprint read and is now hub_systemadmin-only,
    // so it can no longer be the gate for reaching an org's own Templates screen - template:read is
    // what backs the blueprint GET endpoints. Kept as an OR so Catch sysadmins are unaffected.
    // Matches settings-tab.component.ts, which builds the route this entry navigates to.
    this.canReadTemplateAll =
      this.permissionService.hasPermission(this.permission.TEMPLATE_READ) || this.permissionService.hasPermission(this.permission.TEMPLATE_READ_ALL);
    this.canReadAttachment = this.permissionService.hasPermission(this.permission.ATTACHMENT_READ);
    this.canReadLog = this.permissionService.hasPermission(this.permission.LOG_READ);
    this.subscribers.userUpdated = this.userStore.$updatedUser.subscribe(
      (res: any) => {
        if (res) {
          if (res.id === this.user?.id) {
            this.user = res;
            this.cdr.detectChanges();
          }
        }
      },
      (err: any) => {
        console.log(err);
      },
    );
  }

  get userDisplayName() {
    // if (this.user.get('displayName').trim()) {
    //   return this.user.get('displayName');
    // }

    if (this.user && (this.user.firstName.trim() || this.user.lastName.trim())) {
      return this.user.firstName + ' ' + this.user.lastName.trim();
    }
    return this.user?.emailAddress;
  }

  get userInitials(): string {
    const first = this.user?.firstName?.trim()?.charAt(0) ?? '';
    const last = this.user?.lastName?.trim()?.charAt(0) ?? '';
    const initials = (first + last).toUpperCase();
    if (initials) {
      return initials;
    }
    return this.user?.emailAddress?.trim()?.charAt(0)?.toUpperCase() ?? '';
  }

  // setting menu

  // Mirrors the tab priority order in settings-tab.component.ts, so the "Admin settings"
  // heading always lands on a tab the user actually holds instead of a hard-coded one.
  onNavigateToFirstAdminTab() {
    const firstAvailableTab =
      (this.canReadOrg && CHATBOT_AUTO_CLOSE_ROUTES.ORGS_TAB) ||
      (this.isSysAdmin && CHATBOT_AUTO_CLOSE_ROUTES.DIRECTORIES) ||
      (this.canReadPermission && CHATBOT_AUTO_CLOSE_ROUTES.PERMISSION_TAB) ||
      (this.canReadRole && CHATBOT_AUTO_CLOSE_ROUTES.ROLES_TAB) ||
      (this.canReadGroup && CHATBOT_AUTO_CLOSE_ROUTES.GROUPS_TAB) ||
      (this.canReadUser && CHATBOT_AUTO_CLOSE_ROUTES.USERS_TAB) ||
      (this.canReadLicense && CHATBOT_AUTO_CLOSE_ROUTES.LICENSE_TAB) ||
      (this.canCreateExternalLink && CHATBOT_AUTO_CLOSE_ROUTES.EXTERNAL_LINKS_TAB) ||
      (this.canReadApiKey && CHATBOT_AUTO_CLOSE_ROUTES.API_KEYS_TAB) ||
      (this.isSysAdmin && CHATBOT_AUTO_CLOSE_ROUTES.EMAIL_TAB) ||
      (this.isSysAdmin && CHATBOT_AUTO_CLOSE_ROUTES.GLOBAL_TAB) ||
      (this.isSysAdmin && CHATBOT_AUTO_CLOSE_ROUTES.MIGRATIONS_TAB) ||
      (this.canReadTemplateAll && CHATBOT_AUTO_CLOSE_ROUTES.BLUEPRINTS_TAB) ||
      (this.canReadAttachment && CHATBOT_AUTO_CLOSE_ROUTES.ATTACHMENTS_TAB) ||
      (this.canReadLog && CHATBOT_AUTO_CLOSE_ROUTES.LOGS_TAB) ||
      CHATBOT_AUTO_CLOSE_ROUTES.ORGS_TAB;
    this.onNavigateToAdminSettings(firstAvailableTab);
  }

  onNavigateToAdminSettings(tab: string) {
    this.dashboardHelper.setSelectedItem('settingsTab');
    this.dashboardHelper.setShowRightPanel(false);
    this.dashboardHelper.setShowLeftSidePanel(false);
    this.router.navigate(
      [
        '/',
        'dashboard',
        {
          outlets: {
            mainPanel: ['settingsTab'],
            leftSidePanel: ['blank'],
            tabPanel: ['admin', tab],
          },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
    //this.dashboardHelper.setCurrentApplication('Administration');
  }

  onManageprofileandchangepwd() {
    this.dashboardHelper.setShowRightPanel(true);
    this.dashboardHelper.showProfile = true;
    this.router.navigate([
      '/',
      'dashboard',
      {
        outlets: {
          rightPanel: ['profileandchangepwd'],
        },
      },
    ]);
  }

  public onLogout() {
    this.appInitHelper.unloadData();
  }

  onFeedback() {
    this.showFeedbacks();
  }

  showFeedbacks() {
    let dialogRef: MatDialogRef<FeedbackComponent, any>;
    dialogRef = this.matDialog.open(FeedbackComponent, {
      width: '80%',
      height: '80%',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.snackBar.open('Your feedback has been submitted successfully!', undefined, {
          duration: 2000,
        });
      }
    });
  }
}
